import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link2, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n/LanguageContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const KIND_TONE = { customer: "#7c3aed", problem: "#f97316", market: "#3b82f6", solution: "#16a34a" };

function ConnectionRow({ c, currentId, t, navigate }) {
  const others = c.ids.map((id, i) => ({ id, name: c.names[i] })).filter((x) => x.id !== currentId);
  return (
    <li className="rounded-2xl border border-slate-200 p-4" data-testid="connection-item">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full" style={{ color: KIND_TONE[c.kind], background: `${KIND_TONE[c.kind]}14` }}>{t(`connections.kind.${c.kind}`)}</span>
        {c.confidence === "medium" && <span className="text-[10px] text-slate-400 uppercase tracking-wider">{t("connections.medium")}</span>}
      </div>
      <p className="mt-2 text-[15px] font-semibold text-[#0b0f1a] leading-snug">
        {currentId ? others.map((o) => o.name).join(" · ") : c.names.join(" · ")}
      </p>
      <p className="mt-1 text-sm text-slate-600 leading-relaxed">{c.why}</p>
      <div className="mt-3 flex gap-2 flex-wrap items-center">
        {(currentId ? others : c.ids.map((id, i) => ({ id, name: c.names[i] }))).map((o) => (
          <button key={o.id} data-testid={`connection-open-${o.id}`} onClick={() => navigate(`/app/project/${o.id}`)} className="text-xs font-semibold text-[#7c3aed] hover:underline min-h-[44px] px-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7c3aed]">{t("connections.open")} {o.name} →</button>
        ))}
        {c.ids.length >= 2 && (
          <button data-testid={`connection-compare-${c.ids[0]}-${c.ids[1]}`} onClick={() => navigate(`/app/compare/${currentId || c.ids[0]}/${currentId ? others[0].id : c.ids[1]}`)} className="ml-auto text-xs font-semibold text-[#0b0f1a] bg-[#f4f6fa] hover:bg-slate-200 rounded-full px-3 min-h-[36px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7c3aed]">{t("connections.compare")} →</button>
        )}
      </div>
    </li>
  );
}

export function IdeaConnections({ projectId = null, ideasCount = 0 }) {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = (refresh = false) => {
    const req = projectId ? api.get(`/projects/${projectId}/connections`) : api.get(`/ideas/connections`, { params: { refresh, lang } });
    return req.then((r) => setData(r.data));
  };
  useEffect(() => { load().catch(() => setData({ connections: [] })); }, [projectId]); // eslint-disable-line

  const compute = async () => {
    setBusy(true);
    try { await load(true); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };

  const conns = data?.connections || [];
  if (projectId && conns.length === 0) return null;

  return (
    <section className="rounded-3xl border border-slate-200 p-7" data-testid={projectId ? "project-connections" : "home-connections"}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div>
          <span className="cpenz-overline inline-flex items-center gap-1.5"><Link2 size={12} /> {projectId ? t("connections.project_title") : t("connections.title")}</span>
          {!projectId && <p className="mt-2 text-sm text-slate-500">{t("connections.sub")}</p>}
        </div>
        {!projectId && (
          <Button data-testid="connections-compute" onClick={compute} disabled={busy || ideasCount < 2} variant="outline" className="rounded-full h-9 px-4 shrink-0 self-start">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <>{data?.computed_at ? <RefreshCw size={13} className="mr-1.5" /> : null}{data?.computed_at ? t("connections.refresh") : t("connections.find")}</>}
          </Button>
        )}
      </div>
      {!projectId && busy && <p className="mt-4 text-sm text-[#7c3aed]" data-testid="connections-computing">{t("connections.computing")}</p>}
      {!projectId && !busy && ideasCount < 2 && <p className="mt-4 text-sm text-slate-400">{t("connections.need_two")}</p>}
      {!projectId && !busy && ideasCount >= 2 && data?.computed_at && conns.length === 0 && <p className="mt-4 text-sm text-slate-400" data-testid="connections-none">{t("connections.none")}</p>}
      {!projectId && data?.stale && conns.length > 0 && <p className="mt-3 text-xs text-slate-400">{t("connections.stale")}</p>}
      {conns.length > 0 && (
        <ul className="mt-5 grid sm:grid-cols-2 gap-3">
          {conns.map((c, i) => <ConnectionRow key={i} c={c} currentId={projectId} t={t} navigate={navigate} />)}
        </ul>
      )}
    </section>
  );
}
